import { useAccount } from '@/domain/hooks/useAccount'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { SuspenseQueryWith } from '@/utils/types'
import { type UseQueryResult, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useConfig } from 'wagmi'
import { TokensInfo } from './TokenInfo'
import { tokensQueryOptions } from './query'
import { TokenConfig } from './types'

export interface UseTokensParams {
  tokens: TokenConfig[]
  chainId?: number
}

export type UseTokensResult = SuspenseQueryWith<{
  tokensInfo: TokensInfo
}>

export type UseTokensQueryResult = Omit<UseQueryResult<TokensInfo>, 'data'> & {
  tokensInfo: TokensInfo
}

export function useTokensInfo(params: UseTokensParams): UseTokensResult {
  const wagmiConfig = useConfig()
  const account = useAccount()
  const { chainId: pageChainId } = usePageChainId()
  const { tokens } = params
  const chainId = params.chainId ?? pageChainId

  const response = useSuspenseQuery({
    ...tokensQueryOptions({
      tokens,
      wagmiConfig,
      account,
      chainId,
    }),
  })

  return {
    ...response,
    tokensInfo: response.data,
  }
}

export function useTokensInfoQuery(params: UseTokensParams): UseTokensQueryResult {
  const wagmiConfig = useConfig()
  const account = useAccount()
  const { chainId: pageChainId } = usePageChainId()
  const { tokens } = params
  const chainId = params.chainId ?? pageChainId

  const response = useQuery({
    ...tokensQueryOptions({
      tokens,
      wagmiConfig,
      account,
      chainId,
    }),
  })

  return {
    ...response,
    tokensInfo: response.data ?? new TokensInfo([]),
  }
}
